import {
    AlertCircle,
    ArrowRight,
    Briefcase,
    CheckCircle,
    ChevronRight,
    Clock,
    Eye,
    History,
    Home,
    Loader2,
    LocateFixed,
    Map as MapIcon,
    Mic, MicOff,
    Navigation,
    Pencil,
    PlayCircle,
    Plus,
    RotateCcw,
    Save,
    User,
    X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { calculateRateForTrip } from '../lib/rateCalculator';
import { savedAddressService } from '../services/savedAddressService';
import { savedPurposeService } from '../services/savedPurposeService';
import { settingsService } from '../services/settingsService';
import { tripService } from '../services/tripService';
import type { CreateTripDTO, Trip, UpdateTripDTO } from '../types';
import { TripType } from '../types';
import AddressAutocomplete, { type AddressDetails } from './AddressAutocomplete';

interface QuickTripEntryProps {
  onTripSaved: () => void;
  /** All loaded trips from Dashboard – used for same-day trip display. */
  allTrips: Trip[];
}

interface SavedTripConfirmation {
  date: string;
  startAddress: string;
  endAddress: string;
  distance: number;
  purpose: string;
  isBusiness: boolean;
  totalAmount: number;
}

interface AddressEntry {
  id: string;
  label: string;
  address: string;
  isDefault: boolean;
  defaultPurpose?: string | null;
}

const DEFAULT_PURPOSE_CHIPS = [
  'Kundemøde', 'Kundekørsel', 'Konference', 'Kursus',
  'Lager/depot', 'Kontor', 'Lufthavn', 'Sygehus',
];

const MONTH_NAMES = [
  'jan', 'feb', 'mar', 'apr', 'maj', 'jun',
  'jul', 'aug', 'sep', 'okt', 'nov', 'dec',
];

export default function QuickTripEntry({ onTripSaved, allTrips }: QuickTripEntryProps) {
  const { user } = useAuth();

  // Form state
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [fromDropdownId, setFromDropdownId] = useState<string>('');
  const [fromLocked, setFromLocked] = useState(false);
  const [toDropdownId, setToDropdownId] = useState<string>('');
  const [toLocked, setToLocked] = useState(false);
  const [isBusiness, setIsBusiness] = useState(true);
  const [purpose, setPurpose] = useState('');
  const [customPurpose, setCustomPurpose] = useState('');
  const [purposeChips, setPurposeChips] = useState<string[]>(DEFAULT_PURPOSE_CHIPS);
  const [date, setDate] = useState(new Date().toISOString().substring(0, 10));
  const [time, setTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });

  const [fromDetails, setFromDetails] = useState<AddressDetails>({});
  const [toDetails, setToDetails] = useState<AddressDetails>({});

  // Loaded config
  const [distanceKm, setDistanceKm] = useState<number | null>(null);
  const [ratePerKm, setRatePerKm] = useState(3.79);
  const [allAddresses, setAllAddresses] = useState<AddressEntry[]>([]);

  // UI state
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedTrip, setSavedTrip] = useState<SavedTripConfirmation | null>(null);
  const [error, setError] = useState('');
  const [gpsLoading, setGpsLoading] = useState<'from' | 'to' | null>(null);
  const [listeningFor, setListeningFor] = useState<'from' | 'to' | null>(null);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef<unknown>(null);

  // Day-trips list UI
  const [dayTripsExpanded, setDayTripsExpanded] = useState(true);
  const [expandedTripId, setExpandedTripId] = useState<string | null>(null);
  const [editingTripId, setEditingTripId] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<{
    startAddress: string;
    endAddress: string;
    distance: number;
    purpose: string;
    isBusiness: boolean;
    startTime: string;
  } | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const isInitialDateMount = useRef(true);

  // ── Derive same-day trips from the allTrips prop (no extra API call) ─────────
  const dayTrips = useMemo<Trip[]>(() => {
    const forDate = allTrips.filter(t => t.date.startsWith(date));
    return [...forDate].sort((a, b) => {
      const ta = a.startTime ?? a.createdAt.substring(11, 16);
      const tb = b.startTime ?? b.createdAt.substring(11, 16);
      return ta.localeCompare(tb);
    });
  }, [allTrips, date]);

  // Most recent trip's end address (allTrips is ordered date DESC from backend)
  const lastEndAddress = allTrips[0]?.endAddress ?? '';

  // ── Load settings, addresses, rate, purposes on mount ───────────────────────
  useEffect(() => {
    if (!user) return;

    (async () => {
      try {
        const [settings, addrs] = await Promise.all([
          settingsService.getSettings(),
          savedAddressService.getSavedAddresses(),
        ]);
        const homeAddr = settings?.homeAddress?.trim() || '';
        const combined: AddressEntry[] = [
          ...(homeAddr ? [{ id: '__home__', label: 'Hjem', address: homeAddr, isDefault: true, defaultPurpose: null }] : []),
          ...addrs.map(a => ({
            id: a.id, label: a.label, address: a.address,
            isDefault: a.isDefault, defaultPurpose: a.defaultPurpose,
          })),
        ];
        setAllAddresses(combined);
        const firstId = combined[0]?.id || '';
        if (firstId) { setFromDropdownId(firstId); setToDropdownId(firstId); }
      } catch { /* silently continue without saved addresses */ }

      try {
        const currentYear = new Date().getFullYear();
        const fiscalYears = await settingsService.getFiscalYears();
        const fy = fiscalYears.find(y => y.year === currentYear);
        if (fy?.rateUnder20000) setRatePerKm(fy.rateUnder20000);
      } catch { /* fall back to default rate */ }

      try {
        const purposes = await savedPurposeService.getSavedPurposes();
        if (purposes.length > 0) setPurposeChips(purposes.map(p => p.label));
      } catch { /* fall back to default chips */ }
    })();

    setSpeechSupported('webkitSpeechRecognition' in window || 'SpeechRecognition' in window);
  }, [user]);

  // ── Auto-calculate distance when both addresses are filled ───────────────────
  useEffect(() => {
    if (from.length > 5 && to.length > 5) {
      const timer = setTimeout(() => calculateDistance(from, to), 800);
      return () => clearTimeout(timer);
    } else {
      setDistanceKm(null);
    }
  }, [from, to]);

  // ── Clear form when date changes (not on initial render) ─────────────────────
  useEffect(() => {
    if (isInitialDateMount.current) { isInitialDateMount.current = false; return; }
    setFrom(''); setTo('');
    setFromLocked(false); setToLocked(false);
    setPurpose(''); setCustomPurpose('');
    setDistanceKm(null); setError('');
    setExpandedTripId(null); setEditingTripId(null);
  }, [date]);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const calculateDistance = async (origin: string, destination: string) => {
    setCalculating(true);
    setError('');
    try {
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
      const res = await fetch('https://routes.googleapis.com/directions/v2:computeRoutes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey ?? '',
          'X-Goog-FieldMask': 'routes.distanceMeters',
        },
        body: JSON.stringify({
          origin: { address: origin },
          destination: { address: destination },
          travelMode: 'DRIVE',
          routingPreference: 'TRAFFIC_UNAWARE',
          computeAlternativeRoutes: false,
        }),
      });
      const data = await res.json() as { routes?: { distanceMeters?: number }[] };
      const meters = data?.routes?.[0]?.distanceMeters;
      if (meters) setDistanceKm(Math.round(meters / 1000));
    } catch {
      // Silent – user can set km manually
    } finally {
      setCalculating(false);
    }
  };

  const getGpsLocation = async (field: 'from' | 'to') => {
    if (!navigator.geolocation) { setError('GPS ikke understøttet på denne enhed.'); return; }
    setGpsLoading(field);
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000, enableHighAccuracy: true })
      );
      const { latitude, longitude } = pos.coords;
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey ?? ''}&language=da`
      );
      const data = await res.json() as { results?: { formatted_address?: string }[] };
      const address = data?.results?.[0]?.formatted_address || `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
      if (field === 'from') setFrom(address);
      else setTo(address);
    } catch {
      setError('Kunne ikke hente GPS-position. Tillad venligst stedadgang.');
    } finally {
      setGpsLoading(null);
    }
  };

  const startListening = useCallback((field: 'from' | 'to') => {
    const win = window as unknown as Record<string, unknown>;
    const SR = win['SpeechRecognition'] ?? win['webkitSpeechRecognition'];
    if (!SR) return;

    if (recognitionRef.current) {
      (recognitionRef.current as { stop: () => void }).stop();
      recognitionRef.current = null;
      setListeningFor(null);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const recognition = new (SR as any)();
    recognition.lang = 'da-DK';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognitionRef.current = recognition;
    setListeningFor(field);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onresult = (e: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const transcript = String(e.results[0][0].transcript);
      if (field === 'from') setFrom(transcript);
      else setTo(transcript);
      setListeningFor(null);
      recognitionRef.current = null;
    };
    recognition.onerror = () => { setListeningFor(null); recognitionRef.current = null; };
    recognition.onend = () => { setListeningFor(null); recognitionRef.current = null; };
    recognition.start();
  }, []);

  const applyPurposeFromAddress = (addr: AddressEntry) => {
    const p = addr.defaultPurpose || '';
    setPurpose(p);
    setCustomPurpose(p);
  };

  const handleFromDropdownChange = (id: string) => {
    setFromDropdownId(id);
    const found = allAddresses.find(a => a.id === id);
    if (found) { setFrom(found.address); setFromLocked(true); applyPurposeFromAddress(found); }
  };

  const handleToDropdownChange = (id: string) => {
    setToDropdownId(id);
    const found = allAddresses.find(a => a.id === id);
    if (found) { setTo(found.address); setToLocked(true); applyPurposeFromAddress(found); }
  };

  const toggleFromLocked = () => {
    if (fromLocked) { setFromLocked(false); setFrom(''); setPurpose(''); setCustomPurpose(''); }
    else {
      const found = allAddresses.find(a => a.id === fromDropdownId);
      if (found) { setFrom(found.address); setFromLocked(true); applyPurposeFromAddress(found); }
    }
  };

  const toggleToLocked = () => {
    if (toLocked) { setToLocked(false); setTo(''); setPurpose(''); setCustomPurpose(''); }
    else {
      const found = allAddresses.find(a => a.id === toDropdownId);
      if (found) { setTo(found.address); setToLocked(true); applyPurposeFromAddress(found); }
    }
  };

  const continueFromLastTrip = () => {
    const last: Trip | undefined = dayTrips[dayTrips.length - 1];
    if (!last) return;
    const endAddr = last.endAddress;
    const now = new Date();
    setTime(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    const match = allAddresses.find(a => a.address === endAddr);
    if (match) { setFromDropdownId(match.id); setFromLocked(true); setFrom(match.address); applyPurposeFromAddress(match); }
    else { setFrom(endAddr); setFromLocked(false); setPurpose(''); setCustomPurpose(''); }
    setTo(''); setToLocked(false); setDistanceKm(null); setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startInsertAfter = (i: number) => {
    const prev = dayTrips[i];
    const next = dayTrips[i + 1];
    if (!prev) return;
    if (next?.startTime) setTime(next.startTime);
    else {
      const now = new Date();
      setTime(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    }
    const match = allAddresses.find(a => a.address === prev.endAddress);
    if (match) { setFromDropdownId(match.id); setFromLocked(true); setFrom(match.address); applyPurposeFromAddress(match); }
    else { setFrom(prev.endAddress); setFromLocked(false); setPurpose(''); setCustomPurpose(''); }
    setTo(''); setToLocked(false); setDistanceKm(null); setError('');
    setTimeout(() => {
      document.getElementById('quick-trip-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const startEditTrip = (t: Trip) => {
    setEditingTripId(t.id);
    setExpandedTripId(null);
    setEditingValues({
      startAddress: t.startAddress,
      endAddress: t.endAddress,
      distance: Number(t.distance),
      purpose: t.purpose || '',
      isBusiness: t.type === TripType.Business,
      startTime: t.startTime || '—',
    });
  };

  const cancelEdit = () => { setEditingTripId(null); setEditingValues(null); };

  const saveEditTrip = async () => {
    if (!editingValues || !editingTripId) return;
    setSavingEdit(true);
    try {
      const dto: UpdateTripDTO = {
        startAddress: editingValues.startAddress,
        endAddress: editingValues.endAddress,
        distance: editingValues.distance,
        purpose: editingValues.purpose,
        type: editingValues.isBusiness ? TripType.Business : TripType.Personal,
        ...(editingValues.startTime && editingValues.startTime !== '—'
          ? { startTime: editingValues.startTime }
          : {}),
      };
      await tripService.updateTrip(editingTripId, dto);
      setEditingTripId(null);
      setEditingValues(null);
      onTripSaved(); // triggers Dashboard to reload allTrips → dayTrips updates
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Ukendt fejl';
      alert('Fejl ved opdatering: ' + msg);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSave = async () => {
    if (!from.trim() || !to.trim()) { setError('Udfyld venligst fra- og til-adresse.'); return; }
    if (distanceKm === null) { setError('Afstand mangler. Vent på beregning eller opdater adresserne.'); return; }
    setSaving(true);
    setError('');
    try {
      const finalPurpose = customPurpose.trim() || purpose;
      const dto: CreateTripDTO = {
        startAddress: from,
        ...fromDetails,
        endAddress: to,
        ...toDetails,
        distance: distanceKm,
        purpose: finalPurpose,
        date,
        startTime: time,
        type: isBusiness ? TripType.Business : TripType.Personal,
      };
      await tripService.createTrip(dto);
      let totalAmount = distanceKm * ratePerKm;
      if (isBusiness) {
        try {
          const year = new Date(date).getFullYear();
          const rateResult = await calculateRateForTrip(year, distanceKm);
          totalAmount = rateResult.totalAmount;
        } catch {
          // fallback to flat rate if threshold calculation fails
        }
      }
      setSavedTrip({
        date, startAddress: from, endAddress: to, distance: distanceKm,
        purpose: finalPurpose, isBusiness, totalAmount,
      });
      onTripSaved();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Der opstod en fejl ved gemning.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleAddAnother = () => { setSavedTrip(null); };
  const handleReset = () => {
    setFrom(''); setTo(''); setFromLocked(false); setToLocked(false);
    setPurpose(''); setCustomPurpose(''); setDistanceKm(null); setError(''); setSavedTrip(null);
  };

  const effectivePurpose = customPurpose.trim() || purpose;
  const amount = (distanceKm ?? 0) * ratePerKm;
  const isReady = Boolean(from.trim() && to.trim() && distanceKm !== null && !calculating);

  // ── Saved confirmation screen ────────────────────────────────────────────────
  if (savedTrip) {
    const d = new Date(savedTrip.date);
    const displayDate = `${d.getDate()}. ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
    return (
      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl border border-green-200 shadow-sm overflow-hidden">
          <div className="bg-green-50 px-6 py-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-green-800">Kørsel gemt!</h2>
            <p className="text-green-600 text-sm mt-1">{displayDate}</p>
          </div>
          <div className="px-6 py-5 space-y-3">
            <div className="flex items-start gap-3 text-sm">
              <Navigation className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-gray-500 text-xs">Fra</p>
                <p className="font-medium text-gray-800">{savedTrip.startAddress}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-gray-500 text-xs">Til</p>
                <p className="font-medium text-gray-800">{savedTrip.endAddress}</p>
              </div>
            </div>
            <div className="flex gap-4 pt-2 border-t border-gray-100">
              <div>
                <p className="text-xs text-gray-500">Afstand</p>
                <p className="font-bold text-gray-900">{savedTrip.distance.toFixed(1)} km</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Godtgørelse</p>
                <p className="font-bold text-green-700">{savedTrip.totalAmount.toFixed(2)} kr</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Type</p>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${savedTrip.isBusiness ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                  {savedTrip.isBusiness ? 'Erhverv' : 'Privat'}
                </span>
              </div>
            </div>
          </div>
          <div className="px-6 pb-6 flex gap-3">
            <button
              onClick={handleAddAnother}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm transition-colors"
            >
              <PlayCircle className="w-4 h-4" />
              Registrer næste kørsel
            </button>
            <button
              onClick={handleReset}
              className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold text-sm hover:bg-gray-50 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Entry form ────────────────────────────────────────────────────────────────
  return (
    <div id="quick-trip-form" className="max-w-lg mx-auto px-4 py-6 space-y-4">

      {/* Date & time */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-gray-700">Hvornår?</span>
        </div>
        <div className="flex gap-3">
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <input
            type="time"
            value={time}
            onChange={e => setTime(e.target.value)}
            className="w-28 px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Same-day trips */}
      {dayTrips.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl overflow-hidden">
          <button
            onClick={() => setDayTripsExpanded(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-blue-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-800">
                {dayTrips.length} {dayTrips.length === 1 ? 'tur' : 'ture'} registreret
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-blue-600 font-medium">
                {dayTrips.reduce((s, t) => s + Number(t.distance), 0).toFixed(1)} km
                {' · '}
                {dayTrips
                  .filter(t => t.type === TripType.Business)
                  .reduce((s, t) => s + Number(t.distance) * ratePerKm, 0)
                  .toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr
              </span>
              <ChevronRight className={`w-4 h-4 text-blue-400 transition-transform ${dayTripsExpanded ? 'rotate-90' : ''}`} />
            </div>
          </button>

          {dayTripsExpanded && (
            <div className="px-3 pb-3 border-t border-blue-200">
              {dayTrips.map((t, i) => (
                <div key={t.id}>
                  <div className={`mt-2 rounded-xl border ${editingTripId === t.id ? 'border-blue-400 bg-white' : 'border-blue-100 bg-white/60'}`}>

                    {/* Compact row */}
                    {editingTripId !== t.id && (
                      <div
                        className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer"
                        onClick={() => setExpandedTripId(expandedTripId === t.id ? null : t.id)}
                      >
                        <span className="text-blue-400 font-mono w-10 flex-shrink-0">{t.startTime || '—'}</span>
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${t.type === TripType.Business ? 'bg-blue-500' : 'bg-gray-400'}`} />
                        <div className="flex-1 min-w-0 flex items-center gap-1">
                          <span className="text-blue-900 font-medium truncate">{t.startAddress.split(',')[0]}</span>
                          <ArrowRight className="w-3 h-3 text-blue-300 flex-shrink-0" />
                          <span className="text-blue-900 font-medium truncate">{t.endAddress.split(',')[0]}</span>
                        </div>
                        <span className="text-blue-500 font-semibold flex-shrink-0">{Number(t.distance).toFixed(1)} km</span>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                          <button
                            onClick={e => { e.stopPropagation(); setExpandedTripId(expandedTripId === t.id ? null : t.id); }}
                            className="p-1 text-blue-400 hover:text-blue-700 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Se detaljer"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={e => { e.stopPropagation(); startEditTrip(t); }}
                            className="p-1 text-blue-400 hover:text-blue-700 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Rediger"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Expanded detail view */}
                    {expandedTripId === t.id && editingTripId !== t.id && (
                      <div className="px-3 py-2 text-xs space-y-1.5 border-t border-blue-100">
                        <div className="flex gap-2">
                          <span className="text-gray-400 w-14 flex-shrink-0">Fra:</span>
                          <span className="text-gray-800 font-medium">{t.startAddress}</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-400 w-14 flex-shrink-0">Til:</span>
                          <span className="text-gray-800 font-medium">{t.endAddress}</span>
                        </div>
                        <div className="flex gap-4">
                          <div><span className="text-gray-400">Km: </span><span className="font-semibold">{Number(t.distance).toFixed(1)}</span></div>
                          <div>
                            <span className="text-gray-400">Beløb: </span>
                            <span className="font-semibold text-green-700">
                              {(Number(t.distance) * ratePerKm).toFixed(2)} kr
                            </span>
                          </div>
                          <div>
                            <span className={`px-1.5 py-0.5 rounded-full font-medium ${t.type === TripType.Business ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                              {t.type === TripType.Business ? 'Erhverv' : 'Privat'}
                            </span>
                          </div>
                        </div>
                        {t.purpose && <div><span className="text-gray-400">Formål: </span><span className="font-medium">{t.purpose}</span></div>}
                      </div>
                    )}

                    {/* Inline edit form */}
                    {editingTripId === t.id && editingValues && (
                      <div className="px-3 py-3 space-y-2 text-xs">
                        <div className="flex items-center gap-2 mb-1">
                          <Pencil className="w-3.5 h-3.5 text-blue-600" />
                          <span className="font-semibold text-blue-800">Rediger tur</span>
                          <button onClick={cancelEdit} className="ml-auto p-1 text-gray-400 hover:text-gray-600 rounded">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div>
                          <label className="block text-gray-500 mb-1">Tidspunkt (HH:MM)</label>
                          <input
                            type="time"
                            value={editingValues.startTime === '—' ? '' : editingValues.startTime}
                            onChange={e => setEditingValues(v => v ? { ...v, startTime: e.target.value } : v)}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-500 mb-1">Fra</label>
                          <input
                            type="text"
                            value={editingValues.startAddress}
                            onChange={e => setEditingValues(v => v ? { ...v, startAddress: e.target.value } : v)}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-500 mb-1">Til</label>
                          <input
                            type="text"
                            value={editingValues.endAddress}
                            onChange={e => setEditingValues(v => v ? { ...v, endAddress: e.target.value } : v)}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="block text-gray-500 mb-1">Km</label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={editingValues.distance}
                              onChange={e => setEditingValues(v => v ? { ...v, distance: parseFloat(e.target.value) || 0 } : v)}
                              className="w-full px-2 py-1.5 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-gray-500 mb-1">Formål</label>
                            <input
                              type="text"
                              value={editingValues.purpose}
                              onChange={e => setEditingValues(v => v ? { ...v, purpose: e.target.value } : v)}
                              className="w-full px-2 py-1.5 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingValues(v => v ? { ...v, isBusiness: true } : v)}
                            className={`flex-1 py-1.5 rounded-lg font-medium border transition-colors ${editingValues.isBusiness ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 text-gray-600'}`}
                          >
                            Erhverv
                          </button>
                          <button
                            onClick={() => setEditingValues(v => v ? { ...v, isBusiness: false } : v)}
                            className={`flex-1 py-1.5 rounded-lg font-medium border transition-colors ${!editingValues.isBusiness ? 'bg-gray-700 border-gray-700 text-white' : 'border-gray-200 text-gray-600'}`}
                          >
                            Privat
                          </button>
                        </div>
                        <button
                          onClick={saveEditTrip}
                          disabled={savingEdit}
                          className="w-full flex items-center justify-center gap-1.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-50"
                        >
                          <Save className="w-3.5 h-3.5" />
                          {savingEdit ? 'Gemmer...' : 'Gem ændringer'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Insert trip between rows */}
                  {i < dayTrips.length - 1 && (
                    <div className="flex items-center gap-2 my-1 px-1">
                      <div className="flex-1 border-t border-dashed border-blue-200" />
                      <button
                        onClick={() => startInsertAfter(i)}
                        title={`Indsæt tur efter ${t.endAddress.split(',')[0]}`}
                        className="flex items-center gap-1 px-2 py-0.5 text-xs text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded-full border border-blue-200 transition-colors"
                      >
                        <Plus className="w-3 h-3" /> Indsæt her
                      </button>
                      <div className="flex-1 border-t border-dashed border-blue-200" />
                    </div>
                  )}
                </div>
              ))}

              {/* Continue from last trip */}
              <button
                onClick={continueFromLastTrip}
                className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm transition-colors"
              >
                <PlayCircle className="w-4 h-4" />
                Fortsæt fra {(dayTrips[dayTrips.length - 1] as Trip | undefined)?.endAddress.split(',')[0]}
              </button>
            </div>
          )}
        </div>
      )}

      {/* From address */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span className="text-sm font-semibold text-gray-700">Fra</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => getGpsLocation('from')}
              disabled={gpsLoading === 'from'}
              title="Brug nuværende GPS-position"
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              {gpsLoading === 'from' ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
            </button>
            {speechSupported && (
              <button
                onClick={() => startListening('from')}
                title="Dikter adresse"
                className={`p-2 rounded-lg transition-colors ${listeningFor === 'from' ? 'text-red-500 bg-red-50 animate-pulse' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}
              >
                {listeningFor === 'from' ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
              </button>
            )}
          </div>
        </div>

        {allAddresses.length > 0 ? (
          <div className={`flex items-center gap-2 mb-3 p-2.5 rounded-xl border transition-colors ${fromLocked ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
            <input
              type="checkbox"
              checked={fromLocked}
              onChange={toggleFromLocked}
              className="w-4 h-4 rounded accent-blue-600 cursor-pointer flex-shrink-0"
            />
            <Home className={`w-3.5 h-3.5 flex-shrink-0 ${fromLocked ? 'text-blue-600' : 'text-gray-400'}`} />
            <select
              value={fromDropdownId}
              onChange={e => handleFromDropdownChange(e.target.value)}
              className={`flex-1 text-xs font-medium bg-transparent border-none outline-none cursor-pointer min-w-0 ${fromLocked ? 'text-blue-700' : 'text-gray-600'}`}
            >
              {allAddresses.map(a => (
                <option key={a.id} value={a.id}>{a.label}{a.id === '__home__' ? ' 🏠' : ''}</option>
              ))}
            </select>
          </div>
        ) : (
          <p className="text-xs text-gray-400 mb-3 italic">Ingen adresser — tilføj hjemmeadresse under Indstillinger</p>
        )}

        <AddressAutocomplete
          value={from}
          onChange={v => { setFrom(v); if (fromLocked) setFromLocked(false); setFromDetails({}); }}
          onAddressDetails={setFromDetails}
          placeholder="Startadresse..."
          disabled={fromLocked}
        />

        {lastEndAddress && (
          <button
            onClick={() => { setFrom(lastEndAddress); setFromLocked(false); }}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 mt-2 transition-colors"
          >
            <ChevronRight className="w-3 h-3" /> Fra sidst: {lastEndAddress.split(',')[0]}
          </button>
        )}
      </div>

      {/* To address */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-sm font-semibold text-gray-700">Til</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => getGpsLocation('to')}
              disabled={gpsLoading === 'to'}
              title="Brug nuværende GPS-position"
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              {gpsLoading === 'to' ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
            </button>
            {speechSupported && (
              <button
                onClick={() => startListening('to')}
                title="Dikter adresse"
                className={`p-2 rounded-lg transition-colors ${listeningFor === 'to' ? 'text-red-500 bg-red-50 animate-pulse' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}
              >
                {listeningFor === 'to' ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
              </button>
            )}
          </div>
        </div>

        {allAddresses.length > 0 ? (
          <div className={`flex items-center gap-2 mb-3 p-2.5 rounded-xl border transition-colors ${toLocked ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
            <input
              type="checkbox"
              checked={toLocked}
              onChange={toggleToLocked}
              className="w-4 h-4 rounded accent-blue-600 cursor-pointer flex-shrink-0"
            />
            <Home className={`w-3.5 h-3.5 flex-shrink-0 ${toLocked ? 'text-blue-600' : 'text-gray-400'}`} />
            <select
              value={toDropdownId}
              onChange={e => handleToDropdownChange(e.target.value)}
              className={`flex-1 text-xs font-medium bg-transparent border-none outline-none cursor-pointer min-w-0 ${toLocked ? 'text-blue-700' : 'text-gray-600'}`}
            >
              {allAddresses.map(a => (
                <option key={a.id} value={a.id}>{a.label}{a.id === '__home__' ? ' 🏠' : ''}</option>
              ))}
            </select>
          </div>
        ) : (
          <p className="text-xs text-gray-400 mb-3 italic">Ingen adresser — tilføj hjemmeadresse under Indstillinger</p>
        )}

        <AddressAutocomplete
          value={to}
          onChange={v => { setTo(v); if (toLocked) setToLocked(false); setToDetails({}); }}
          onAddressDetails={setToDetails}
          placeholder="Slutadresse..."
          disabled={toLocked}
        />
      </div>

      {/* Distance */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">Beregnet afstand</span>
          {calculating && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
        </div>
        {distanceKm !== null ? (
          <>
            <div className="flex items-center gap-3 mt-2">
              <input
                type="number"
                min="0"
                step="0.1"
                value={distanceKm}
                onChange={e => setDistanceKm(parseFloat(e.target.value) || 0)}
                className="w-28 px-3 py-2 border border-gray-200 rounded-xl text-lg font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="text-gray-500">km</span>
              {from && to && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(from)}&destination=${encodeURIComponent(to)}&travelmode=driving`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors text-xs font-medium"
                >
                  <MapIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  Åben ruten i Maps
                </a>
              )}
              <span className="text-green-700 font-bold ml-auto">{amount.toFixed(2)} kr</span>
            </div>
            <p className="text-xs text-gray-400 mt-1.5 italic">
              Vælger du en anden rute i Maps, ret km manuelt i feltet ovenfor.
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-400 mt-2">
            {from && to ? 'Beregner...' : 'Udfyld begge adresser'}
          </p>
        )}
      </div>

      {/* Business / Personal */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
        <span className="text-sm font-semibold text-gray-700 block mb-3">Type</span>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setIsBusiness(true)}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-colors border-2 ${isBusiness ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'}`}
          >
            <Briefcase className="w-4 h-4" /> Erhverv
          </button>
          <button
            onClick={() => setIsBusiness(false)}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-colors border-2 ${!isBusiness ? 'bg-gray-700 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'}`}
          >
            <User className="w-4 h-4" /> Privat
          </button>
        </div>
      </div>

      {/* Purpose */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
        <span className="text-sm font-semibold text-gray-700 block mb-3">
          Formål <span className="text-gray-400 font-normal">(valgfrit)</span>
        </span>
        {purposeChips.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {purposeChips.map(chip => (
              <button
                key={chip}
                onClick={() => {
                  if (purpose === chip) { setPurpose(''); setCustomPurpose(''); }
                  else { setPurpose(chip); setCustomPurpose(chip); }
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${purpose === chip ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-700'}`}
              >
                {chip}
              </button>
            ))}
          </div>
        )}
        <input
          type="text"
          value={customPurpose}
          onChange={e => { setCustomPurpose(e.target.value); setPurpose(''); }}
          placeholder="Skriv eget formål..."
          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Save button */}
      <button
        onClick={handleSave}
        disabled={!isReady || saving}
        className={`w-full py-4 rounded-2xl font-bold text-base transition-all flex items-center justify-center gap-2 ${isReady && !saving ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
      >
        {saving
          ? <><Loader2 className="w-5 h-5 animate-spin" /> Gemmer...</>
          : <><CheckCircle className="w-5 h-5" /> Gem kørsel {effectivePurpose ? `— ${effectivePurpose}` : ''}</>}
      </button>

      <p className="text-center text-xs text-gray-400 pb-4">
        Sats: {ratePerKm.toFixed(2)} kr/km · Ændres under Indstillinger
      </p>
    </div>
  );
}
